// Need to use the React-specific entry point to import createApi
import forOwn from 'lodash/forOwn';
import isArray from 'lodash/isArray';
import isBoolean from 'lodash/isBoolean';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';

import {apiSlice} from './apiSlice';

const stateDict = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  lousiania: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north caronlia': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklohoma: 'OK',
  oregon: 'OR',
  pennslyvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermount: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
};

const fieldMappings = {
  id: 'id',
  user: 'user',
  facility: 'facility',
  firstName: 'first_name',
  lastName: 'last_name',
  honorific: 'honorific', // e.g. Dr.
  title: 'title', // e.g. PhD, MD, PT
  city: 'address_city',
  state: 'address_state',
  pronouns: 'personal_pronouns',
  bio: 'bio',
  fun_facts: 'fun_facts',
  education: 'education',
  credentials: 'credentials',
  gender: 'gender',
  hourlyRate: 'rate',
  linkedInUrl: 'linkedin_url',
  instagramUrl: 'instagram_url',
  imgSrc: 'public_profile_img_url',
  mapImgUrl: 'public_map_img_url',
  youtube_url: 'youtube_url',
  // New fields
  experienceCategories: 'experience_category',
  specialtyCategories: 'specialty_categories',
  locationCategories: 'location_categories',
  treatmentCategories: 'treatment_categories',
  location_strings: 'location_strings',
  reviews: 'reviews', // {review: {rating, feedback, date}, patient: {firstName, lastName}}
  slug: 'slug',
  is_verified: 'is_verified',
  // TODO
  whatITreat: 'what_i_treat',
  howITreat: 'how_i_treat',
  highlights: 'highlights',
  primary_location_city: 'primary_location_city',
  primary_location_state: 'primary_location_state',
  is_patient: 'is_patient',
  is_provider: 'is_provider',
  who_i_treat: 'who_i_treat',
  timezone: 'timezone',
  rate: 'rate',
  reviews: 'reviews',
  specializes_in: 'specializes_in',
  // mapImgSrc: '',
  // cities: '',
  // images: '',
};

// const reverseFieldMappings = Object.keys(fieldMappings).reduce((acc, k) => ({
//   ...acc,
//   [fieldMappings[k]]: k,
// }), {});

const searchKeys = {
  id: 'id',
  skip: 'skip',
  limit: 'limit',
  sort: 'sort',
  slug: 'slug',
  text: 'text',
  gender: 'gender',
  experience: 'experience_category',
  location: 'location_categories',
  specialty: 'specialty_categories',
  treatments: 'treatment_categories',
  patient_id: 'patient_id',
  is_hidden: 'is_hidden',
  include_reviews: 'include_reviews',
  is_publicly_searchable: 'is_publicly_searchable',
  ids: 'ids',
  save_search: 'save_search',
  search_uuid: 'search_uuid',
  lat: 'lat',
  lon: 'lon',
  radius: 'radius',
};

const mapProviderFromApi =  p => (
  Object.keys(fieldMappings).reduce((acc, k) => ({
    ...acc,
    [k]: p[fieldMappings[k]],
  }), {})
);

const mapFieldsToApi = body => {
  const data = Object.keys(body).reduce((acc, k) => ({
    ...acc,
    [fieldMappings[k]]: body[k],
  }), {});
  return data;
};

const parseText = text => {
  if (!text) return text;
  let cleanText = text.trim();
  if (stateDict[cleanText.toLowerCase()]) return stateDict[cleanText.toLowerCase()];
  return cleanText;
};

// Define a service using a base URL and expected endpoints
export const providerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProviders: builder.query({
      query: (filters) => {
        let qs = '?';
        const _filters = {...filters, text: parseText(filters.text)};
        forOwn(_filters, (value, _key) => {
          let key = searchKeys[_key];
          if (isArray(value)) {
            if (value.length) {
              qs += `${key}=${encodeURI(value.join(','))}&`;
            }
          } else if (
            isNumber(value)
            || (isString(value) && value.length) 
            || isBoolean(value)
          ) {
            qs += `${key}=${encodeURI(value)}&`;
          }
        });
        return `provider/${qs}`;
      },
      transformResponse: res => res.map(mapProviderFromApi),
      providesTags: ['Provider', 'SessionType'],
    }),
    getProviderById: builder.query({
      query: (id) => `provider/${id}`,
      transformResponse: mapProviderFromApi,
      providesTags: ['Provider', 'SessionType'],
    }),
    getCurrentProvider: builder.query({
      query: (id) => `provider/current/`,
      providesTags: ['Provider'],
    }),
    createProvider: builder.mutation({
      query: body => ({
        url: 'provider/',
        method: 'POST',
        body: {...body, email: (body.email || '').toLowerCase()},
      }),
      invalidatesTags: ['Provider'],
    }),
    updateProvider: builder.mutation({
      query: ({ id, body }) => ({
        url: `provider/${id}/`,
        method: 'PUT',
        body: body,
      }),
      invalidatesTags: ['Provider'],
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetCurrentProviderQuery,
  useGetProvidersQuery,
  useGetProviderByIdQuery,
  useCreateProviderMutation,
  useUpdateProviderMutation
} = providerApi;
